import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/auth/useAccessToken";

// Live mailbox updates over the BFF's server-sent-events stream (GET /api/mail/events). The list and folder counts used to be
// polled every 20 seconds; now the server says "something changed" and only then do we refetch. EventSource can't send the
// bearer token, so the stream is read with fetch. While it is up the old polls slow to a safety net; if it drops they resume.

const base = import.meta.env.VITE_MAIL_BFF_BASE_URL as string | undefined;

let connected = false;
const listeners = new Set<() => void>();
const setConnected = (value: boolean) => {
    if (connected === value) return;
    connected = value;
    listeners.forEach((l) => l());
};

/** True while the push stream is open. Queries read this to decide how often to poll. */
export const isMailPushConnected = () => connected;

/** The poll interval for mailbox queries: a slow safety net while push is up, the old 20 s otherwise. */
export const mailPollInterval = () => (connected ? 5 * 60_000 : 20_000);

interface Frame { event: string; data: string }

/** Splits an SSE chunk buffer into complete frames; returns the unconsumed remainder. */
export function parseFrames(buffer: string): { frames: Frame[]; rest: string } {
    const frames: Frame[] = [];
    const parts = buffer.split(/\r?\n\r?\n/);
    const rest = parts.pop() ?? "";
    for (const part of parts) {
        let event = "message";
        const data: string[] = [];
        for (const line of part.split(/\r?\n/)) {
            if (line.startsWith(":") || line.length === 0) continue; // comment / keep-alive
            if (line.startsWith("event:")) event = line.slice(6).trim();
            else if (line.startsWith("data:")) data.push(line.slice(5).trimStart());
        }
        if (data.length > 0 || event !== "message") frames.push({ event, data: data.join("\n") });
    }
    return { frames, rest };
}

/**
 * Keeps one push stream open while the shell is mounted and turns each event into a refetch of the lists it affects
 * (only of queries that are on screen: invalidation doesn't touch inactive ones).
 */
export function useMailPush() {
    const qc = useQueryClient();
    const token = useAccessToken();

    useEffect(() => {
        if (!base || !token) return;
        const stop = new AbortController();
        let coalesce: number | undefined;

        // A burst of events (a rule moving ten messages) becomes one refetch.
        const refresh = () => {
            window.clearTimeout(coalesce);
            coalesce = window.setTimeout(() => {
                void qc.invalidateQueries({ queryKey: ["messages"] });
                void qc.invalidateQueries({ queryKey: ["folders"] });
                void qc.invalidateQueries({ queryKey: ["search"] });
            }, 250);
        };

        const run = async () => {
            let delay = 1_000;
            while (!stop.signal.aborted) {
                try {
                    const res = await fetch(new URL("/api/mail/events", base).toString(), {
                        headers: { Authorization: `Bearer ${token}`, Accept: "text/event-stream" },
                        signal: stop.signal
                    });
                    if (res.status === 429) {
                        // Too many streams for this mailbox: back off, polling covers us meanwhile.
                        delay = Math.max(delay, Number(res.headers.get("Retry-After") ?? 30) * 1000);
                        throw new Error("busy");
                    }
                    if (!res.ok || !res.body) throw new Error(`push ${res.status}`);
                    setConnected(true);
                    delay = 1_000;
                    refresh(); // anything missed while we were not connected
                    const reader = res.body.getReader();
                    const decoder = new TextDecoder();
                    let buffer = "";
                    for (;;) {
                        const { done, value } = await reader.read();
                        if (done) break;
                        buffer += decoder.decode(value, { stream: true });
                        const { frames, rest } = parseFrames(buffer);
                        buffer = rest;
                        if (frames.some((f) => f.event === "mail" || f.event === "flags" || f.event === "folders")) refresh();
                    }
                } catch {
                    /* dropped or refused: fall through to back off and reconnect */
                }
                setConnected(false);
                if (stop.signal.aborted) break;
                await new Promise((r) => window.setTimeout(r, delay));
                delay = Math.min(delay * 2, 30_000);
            }
        };
        void run();

        return () => {
            stop.abort();
            window.clearTimeout(coalesce);
            setConnected(false);
        };
    }, [qc, token]);
}
